import { useEffect, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { loadOwnBriefs, isOwnBriefsLoaded } from 'redux/modules/portfolio';

/**
 * Resolved briefs for the logged-in viewer (browse invite / hire flows).
 *
 * @param {object|null} authUser
 * @param {object} portfolio
 * @param {{ collaborations?: unknown[] }} ownBriefs
 * @param {Record<string, unknown>} briefEntities
 */
export function useViewerCollaborations(
  authUser,
  portfolio,
  ownBriefs,
  briefEntities
) {
  const dispatch = useDispatch();

  useEffect(() => {
    if (authUser && authUser.username) {
      dispatch(loadOwnBriefs(authUser.username));
    }
  }, [authUser, dispatch]);

  const collaborationIds = useMemo(() => {
    const fromAuth = ownBriefs.collaborations;
    if (Array.isArray(fromAuth) && fromAuth.length > 0) {
      return fromAuth;
    }
    const fromPortfolio =
      authUser &&
      authUser.username &&
      portfolio[authUser.username] &&
      portfolio[authUser.username].ownBriefs;
    if (Array.isArray(fromPortfolio) && fromPortfolio.length > 0) {
      return fromPortfolio;
    }
    return [];
  }, [ownBriefs.collaborations, authUser, portfolio]);

  const collaborations = useMemo(
    () =>
      collaborationIds
        .map((entry) => {
          if (entry && typeof entry === 'object' && entry.id != null) {
            return entry;
          }
          const key = entry != null ? String(entry) : entry;
          return briefEntities[entry] || briefEntities[key];
        })
        .filter(Boolean),
    [collaborationIds, briefEntities]
  );

  const collaborationsRef = useRef(collaborations);
  collaborationsRef.current = collaborations;

  const isLoadingOwnBriefs =
    !!authUser &&
    (!!portfolio[authUser.username]?.loadingOwnBriefs ||
      !isOwnBriefsLoaded({ portfolio }, authUser.username));

  const isLoadingOwnBriefsRef = useRef(isLoadingOwnBriefs);
  isLoadingOwnBriefsRef.current = isLoadingOwnBriefs;

  const collaborationsHydrationSignature = useMemo(
    () =>
      collaborations
        .map((b) => `${b.id}:${b.text == null ? '' : String(b.text)}`)
        .join('|'),
    [collaborations]
  );

  return {
    collaborations,
    collaborationsRef,
    isLoadingOwnBriefs,
    isLoadingOwnBriefsRef,
    collaborationsHydrationSignature,
  };
}
